import React from 'react';
import { Link as RRLink } from 'react-router-dom';
import { Menu, MenuItem, ProSidebar, SidebarContent, SidebarFooter, SubMenu } from 'react-pro-sidebar';
import * as ROUTES from '../../lib/routing/frontend';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { NAV_NEW, NAV_OLD, isNewNav, setNavStructure, toggledNavStructure } from '../../lib/helper/navPreference';
import {
  canUploadInvoices,
  isAdmin,
  isChaserOutsourcer,
  isClient,
  isConsultant,
  isOutsourcer,
  isSeniorStaff,
  isStaff,
  isSubUser,
  subUserCanUploadBills,
  subUserCanViewAndDownloadInvoices,
  subUserCanViewAndDownloadReports,
} from '../../lib/helper/authorisation';
import { invoiceListClearFilters } from '../../lib/redux/actions/invoices';
import { problemDataListClearFilters } from '../../lib/redux/actions/problemData';
import { invoiceBatchListClearFilters } from '../../lib/redux/actions/invoiceBatch';
import { consultantListClearFilters } from '../../lib/redux/actions/consultant';
import { userListClearFilters } from '../../lib/redux/actions/userList';
import { ccsdCodeListClearFilters } from '../../lib/redux/actions/ccsdCodes';

const Link = (props) => <RRLink {...props} />;

const isRouteActive = (currentPath, menuRoute) => {
  if (!menuRoute || !currentPath) return false;

  // Remove parameters from route for comparison
  const cleanRoute = menuRoute.replace(/\/:[^/]+\??/g, '').replace(/\?/, '');

  // Handle exact matches
  if (currentPath === cleanRoute) return true;

  // Handle routes with parameters (e.g., /invoices/1 should match /invoices)
  if (cleanRoute !== '/' && currentPath.startsWith(cleanRoute)) {
    // Check if the remaining path is just a parameter
    const remaining = currentPath.substring(cleanRoute.length);
    return remaining === '' || remaining.match(/^\/[^/]+$/) || remaining.match(/^\/[^/]+\/.*$/);
  }

  return false;
};

const mapStateToProps = (state) => ({
  ...state.stats,
  ...state.auth,
  ...state.subUserSingle,
});

const mapDispatchToProps = {
  invoiceListClearFilters,
  invoiceBatchListClearFilters,
  problemDataListClearFilters,
  consultantListClearFilters,
  userListClearFilters,
  ccsdCodeListClearFilters,
};

class Sidebar extends React.Component {
  constructor(props) {
    super(props);

    // Only there so toggling the menu structure re-renders. The preference itself lives in
    // localStorage, read through isNewNav() at the point of use - holding a copy here as well
    // would give two sources of truth for one boolean.
    this.state = { navVersion: 0 };
  }

  toggleNavStructure() {
    setNavStructure(toggledNavStructure(isNewNav() ? NAV_NEW : NAV_OLD));
    this.setState((prev) => ({ navVersion: prev.navVersion + 1 }));
  }

  getWrappedLink(linkTo, clickEvent = () => {}) {
    const { toggleSidebar } = this.props;
    return (
      <Link
        to={linkTo}
        onClick={() => {
          clickEvent();
          toggleSidebar();
        }}
      />
    );
  }

  /**
   * The admin sidebar, in two arrangements.
   *
   * Every menu item below is defined exactly once, in a `nav*` method, and the two structures
   * compose those methods. That is the point of the split: a route, a badge or a filter-clearing
   * callback cannot drift between the old menu and the new one, because there is only ever one of
   * each. See navPreference.js for which one renders.
   */
  renderAdminMenu() {
    return isNewNav() ? this.renderAdminMenuNew() : this.renderAdminMenuOld();
  }

  /**
   * The menu as it has always been: fifteen top-level entries for an admin.
   */
  renderAdminMenuOld() {
    return (
      <>
        <SubMenu title="Billing Checklist" icon={<i className="fas fa-calendar-check"></i>}>
          {this.navBillingChecklistItems()}
        </SubMenu>
        <SubMenu title="Outsourcer Scheduling" icon={<i className="fas fa-calendar"></i>}>
          {this.navOutsourcerScheduleItems()}
        </SubMenu>

        {this.navWorkQueueItems()}
        {this.navCoreWorkflow()}
        {this.navReportsSubMenu()}
        <SubMenu title="Accounts" icon={<i className="fas fa-user" />}>
          {this.navConsultantItems()}
          <li>
            <hr className="my-1 mx-3" />
          </li>
          {this.navUserItems()}
          <li>
            <hr className="my-1 mx-3" />
          </li>
          {this.navHospitalItems()}
        </SubMenu>
        {this.navConsultantTicketsSubMenu()}
        <SubMenu title="MediCodes" icon={<i className="fas fa-book-medical" />}>
          {this.navCcsdItems()}
          <li>
            <hr className="my-1 mx-3" />
          </li>
          {this.navInsurerItem()}
        </SubMenu>
        <SubMenu title="Info" icon={<i className="fas fa-info" />}>
          {this.navGuidanceItems()}
        </SubMenu>
      </>
    );
  }

  /**
   * The regrouped menu: ten top-level entries.
   *
   * What moved, and why:
   *
   * - **My Work** collects the three badge-bearing queues that used to float loose in the middle
   *   of the list. One place that answers "what needs me today".
   * - **Consultant Tickets** stays top level, directly under My Work. It is a submenu in its own
   *   right, and nesting one submenu inside another buried its three entries two levels deep.
   * - **Records** replaces Accounts, which held consultants, users and hospitals together behind
   *   a name that fits only one of them. Everything here is a register with an index and a detail
   *   screen, which is what finally gives Patients somewhere to live - it was hanging off the
   *   bottom of the sidebar next to Refer a Colleague.
   * - **Scheduling** merges the two calendars, Billing Checklist and Outsourcer Scheduling.
   * - **Guidance** is the old Info, named for what it holds.
   *
   * Invoice Management, Credit Control and Extended Workflow are untouched, deliberately.
   */
  renderAdminMenuNew() {
    return (
      <>
        <SubMenu title="My Work" icon={<i className="fas fa-inbox" />} defaultOpen={true}>
          {this.navWorkQueueItems()}
        </SubMenu>

        {this.navConsultantTicketsSubMenu()}

        {this.navCoreWorkflow()}

        <SubMenu title="Records" icon={<i className="fas fa-folder-open" />}>
          {this.navPatientsItem()}
          {this.navSuppressedShortfallsItem()}
          <li>
            <hr className="my-1 mx-3" />
          </li>
          {this.navConsultantItems()}
          <li>
            <hr className="my-1 mx-3" />
          </li>
          {this.navHospitalItems()}
          {this.navInsurerItem()}
          <li>
            <hr className="my-1 mx-3" />
          </li>
          {this.navCcsdItems()}
          <li>
            <hr className="my-1 mx-3" />
          </li>
          {this.navUserItems()}
        </SubMenu>

        <SubMenu title="Scheduling" icon={<i className="fas fa-calendar-check" />}>
          {this.navBillingChecklistItems()}
          <li>
            <hr className="my-1 mx-3" />
          </li>
          {this.navOutsourcerScheduleItems()}
        </SubMenu>

        {this.navReportsSubMenu()}

        <SubMenu title="Guidance" icon={<i className="fas fa-info" />}>
          {this.navGuidanceItems()}
        </SubMenu>
      </>
    );
  }

  // --- the blocks both structures are built from ------------------------------

  navBillingChecklistItems() {
    const { location } = this.props;
    return (
      <>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.BILLING_CHECKLIST.replace('/:date', ''))}>
          Billing Checklist{this.getWrappedLink(ROUTES.BILLING_CHECKLIST.replace('/:date', ''))}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.BILLING_CHECKLIST_EVENTS.ALL.replace('/:page', ''))}>
          View Events/Holidays{this.getWrappedLink(ROUTES.BILLING_CHECKLIST_EVENTS.ALL.replace('/:page', ''))}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.BILLING_CHECKLIST_EVENTS.CREATE)}>
          Create Event/Holiday{this.getWrappedLink(ROUTES.BILLING_CHECKLIST_EVENTS.CREATE)}
        </MenuItem>
      </>
    );
  }

  navOutsourcerScheduleItems() {
    const { location } = this.props;
    return (
      <>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.WORK_SCHEDULE.replace('/:date', ''))}>
          Outsourcers{this.getWrappedLink(ROUTES.WORK_SCHEDULE.replace('/:date', ''))}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.WORK_SCHEDULE_EVENTS.ALL.replace('/:page', ''))}>
          View all Holidays{this.getWrappedLink(ROUTES.WORK_SCHEDULE_EVENTS.ALL.replace('/:page', ''))}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.WORK_SCHEDULE_EVENTS.CREATE)}>
          Add a Holiday{this.getWrappedLink(ROUTES.WORK_SCHEDULE_EVENTS.CREATE)}
        </MenuItem>
      </>
    );
  }

  navWorkQueueItems() {
    const { stats, invoiceBatchListClearFilters, location } = this.props;
    return (
      <>
        <MenuItem
          icon={<i className="fas fa-inbox" />}
          suffix={stats.invoicesToAccept && stats.invoicesToAccept > 0 ? <span className="badge badge-pill badge-danger">{stats.invoicesToAccept}</span> : ''}
          active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.TO_ACCEPT.replace('/:page', ''))}>
          Mine to Accept{this.getWrappedLink(ROUTES.INVOICE_BATCHES.TO_ACCEPT.replace('/:page', ''))}
        </MenuItem>
        <MenuItem
          icon={<i className="fas fa-spinner" />}
          suffix={
            stats.invoicesToProcess && stats.invoicesToProcess > 0 ? <span className="badge badge-pill badge-danger">{stats.invoicesToProcess}</span> : ''
          }
          active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.TO_PROCESS.replace('/:page', ''))}>
          Mine to Process{this.getWrappedLink(ROUTES.INVOICE_BATCHES.TO_PROCESS.replace('/:page', ''))}
        </MenuItem>
        <MenuItem
          icon={<i className="fas fa-exclamation-circle text-danger" />}
          suffix={
            stats.problemInvoiceBatches && stats.problemInvoiceBatches > 0 ? (
              <span className="badge badge-pill badge-danger">{stats.problemInvoiceBatches}</span>
            ) : (
              ''
            )
          }
          active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.PROBLEM.replace('/:page', ''))}>
          Problem Batches
          {this.getWrappedLink(ROUTES.INVOICE_BATCHES.PROBLEM.replace('/:page', ''), () => {
            invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.PROBLEM);
          })}
        </MenuItem>
      </>
    );
  }

  /**
   * Invoice Management, Credit Control and Extended Workflow, exactly as they were.
   */
  navCoreWorkflow() {
    const { stats, invoiceListClearFilters, invoiceBatchListClearFilters, problemDataListClearFilters, location } = this.props;
    return (
      <>
        <SubMenu title="Invoice Management" icon={<i className="fas fa-list" />}>
          <MenuItem
            suffix={
              stats.urgentInvoiceBatchesToComplete && stats.urgentInvoiceBatchesToComplete > 0 ? (
                <span className="badge badge-pill badge-danger">{stats.urgentInvoiceBatchesToComplete}</span>
              ) : (
                ''
              )
            }
            active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.URGENT.replace('/:page', ''))}>
            Urgent Invoice Batches
            {this.getWrappedLink(ROUTES.INVOICE_BATCHES.URGENT.replace('/:page', ''), () => {
              invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.URGENT);
            })}
          </MenuItem>
          <MenuItem
            suffix={
              stats.invoicesToCheckAndPrice && stats.invoicesToCheckAndPrice > 0 ? (
                <span className="badge badge-pill badge-danger">{stats.invoicesToCheckAndPrice}</span>
              ) : (
                ''
              )
            }
            active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.TO_CHECK_AND_PRICE.replace('/:page', ''))}>
            To Check and Price
            {this.getWrappedLink(ROUTES.INVOICE_BATCHES.TO_CHECK_AND_PRICE.replace('/:page', ''), () => {
              invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.TO_CHECK_AND_PRICE);
            })}
          </MenuItem>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.BULK_ASSIGN.replace('/:page', ''))}>
            Bulk Assign Invoices
            {this.getWrappedLink(ROUTES.INVOICE_BATCHES.BULK_ASSIGN.replace('/:page', ''), () => {
              invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.BULK_ASSIGN);
            })}
          </MenuItem>
          <MenuItem
            suffix={stats.invoicesToAssign && stats.invoicesToAssign > 0 ? <span className="badge badge-pill badge-danger">{stats.invoicesToAssign}</span> : ''}
            active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.UNASSIGNED.replace('/:page', ''))}>
            Unassigned Invoice Batches
            {this.getWrappedLink(ROUTES.INVOICE_BATCHES.UNASSIGNED.replace('/:page', ''), () => {
              invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.UNASSIGNED);
            })}
          </MenuItem>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''))}>
            All Invoice Batches
            {this.getWrappedLink(ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''), () => {
              invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.ALL);
            })}
          </MenuItem>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.CREATE)}>
            Upload Invoices{this.getWrappedLink(ROUTES.INVOICE_BATCHES.CREATE)}
          </MenuItem>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.EXPORT_INVOICE_NUMBERS)}>
            Export Invoice Numbers{this.getWrappedLink(ROUTES.EXPORT_INVOICE_NUMBERS)}
          </MenuItem>
        </SubMenu>
        <SubMenu title="Credit Control" icon={<i className="fas fa-file-invoice" />}>
          <SubMenu title="Credit Control" icon={<i className="fas fa-file-invoice" />}>
            <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.PARSE)}>Upload An Invoice{this.getWrappedLink(ROUTES.INVOICES.PARSE)}</MenuItem>
            <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.CREATE)}>Match Invoices{this.getWrappedLink(ROUTES.INVOICES.CREATE)}</MenuItem>
            <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.UPDATE)}>Payments Upload{this.getWrappedLink(ROUTES.INVOICES.UPDATE)}</MenuItem>
            {/*<MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.PARSE_SELF_PAY)}>*/}
            {/*  Upload New CC Invoice{this.getWrappedLink(ROUTES.INVOICES.PARSE_SELF_PAY)}*/}
            {/*</MenuItem>*/}
            <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.UNPAID.replace('/:page', ''))}>
              All UnPaid Invoices{this.getWrappedLink(ROUTES.INVOICES.UNPAID.replace('/:page', ''), () => invoiceListClearFilters(ROUTES.INVOICES.UNPAID))}
            </MenuItem>
            <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.ALL.replace('/:page', ''))}>
              All Invoices{this.getWrappedLink(ROUTES.INVOICES.ALL.replace('/:page', ''), () => invoiceListClearFilters(ROUTES.INVOICES.ALL))}
            </MenuItem>
            <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICE_NOTIFICATION_EVENTS.ALL.replace('/:page', ''))}>
              Failed Invoice Notifications {this.getWrappedLink(ROUTES.INVOICE_NOTIFICATION_EVENTS.ALL.replace('/:page', ''))}
            </MenuItem>
          </SubMenu>

          {/*<li><hr className={'my-1 mr-3'}/></li>*/}
          <SubMenu title="Self Pay" icon={<i className="fas fa-file-prescription text-info " />}>
            <MenuItem
              suffix={
                stats.escalatedSelfPayInvoices && stats.escalatedSelfPayInvoices > 0 ? (
                  <span className="badge badge-pill badge-danger">{stats.escalatedSelfPayInvoices}</span>
                ) : (
                  ''
                )
              }
              active={isRouteActive(location.pathname, ROUTES.INVOICES.ESCALATED_SELF_PAY.replace('/:page', ''))}>
              Escalated Invoices
              {this.getWrappedLink(ROUTES.INVOICES.ESCALATED_SELF_PAY.replace('/:page', ''), () => {
                invoiceListClearFilters(ROUTES.INVOICES.ESCALATED_SELF_PAY);
              })}
            </MenuItem>

            <MenuItem
              suffix={stats.insUpd && stats.insUpd > 0 ? <span className="badge badge-pill badge-danger">{stats.insUpd}</span> : ''}
              active={isRouteActive(location.pathname, ROUTES.INVOICES.WITH_UPDATED_INSURANCE_DETAILS.replace('/:page', ''))}>
              Insurance Detail Updates {this.getWrappedLink(ROUTES.INVOICES.WITH_UPDATED_INSURANCE_DETAILS.replace('/:page', ''))}
            </MenuItem>
            <MenuItem
              suffix={
                stats.creditControlInvoicesMissingContactDetails && stats.creditControlInvoicesMissingContactDetails > 0 ? (
                  <span className="badge badge-pill badge-danger">{stats.creditControlInvoicesMissingContactDetails}</span>
                ) : (
                  ''
                )
              }
              active={isRouteActive(location.pathname, ROUTES.INVOICES.MISSING_CONTACT_DETAILS.replace('/:page', ''))}>
              Missing Contact Details
              {this.getWrappedLink(ROUTES.INVOICES.MISSING_CONTACT_DETAILS.replace('/:page', ''), () => {
                invoiceListClearFilters(ROUTES.INVOICES.MISSING_CONTACT_DETAILS);
              })}
            </MenuItem>
            {/*<MenuItem*/}
            {/*    suffix={*/}
            {/*      stats.creditControlInvoicesUnassigned && stats.creditControlInvoicesUnassigned > 0 ? (*/}
            {/*          <span className="badge badge-pill badge-danger">{stats.creditControlInvoicesUnassigned}</span>*/}
            {/*      ) : (*/}
            {/*          ''*/}
            {/*      )*/}
            {/*    }>*/}
            {/*  Bulk Assign Work{this.getWrappedLink(ROUTES.INVOICES.BULK_ASSIGN.replace('/:page', ''), () => {*/}
            {/*  invoiceListClearFilters(ROUTES.INVOICES.BULK_ASSIGN);*/}
            {/*})}*/}

            {/*</MenuItem>*/}

            {/*<MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.PARSE_SELF_PAY)}>*/}
            {/*  Upload Self Pay Invoice{this.getWrappedLink(ROUTES.INVOICES.PARSE_SELF_PAY)}*/}
            {/*</MenuItem>*/}
            {/*<MenuItem>Chaser Work Schedules{this.getWrappedLink(ROUTES.CHASER_WORK_SCHEDULE.replace('/:date', ''))}</MenuItem>*/}
          </SubMenu>

          <SubMenu title="Hospital Chasing" icon={<i className="fas fa-file-medical text-danger" />}>
            <MenuItem
              suffix={
                stats.escalatedHospitalInvoices && stats.escalatedHospitalInvoices > 0 ? (
                  <span className="badge badge-pill badge-danger">{stats.escalatedHospitalInvoices}</span>
                ) : (
                  ''
                )
              }
              active={isRouteActive(location.pathname, ROUTES.INVOICES.ESCALATED_HOSPITAL.replace('/:page', ''))}>
              Escalated Invoices
              {this.getWrappedLink(ROUTES.INVOICES.ESCALATED_HOSPITAL.replace('/:page', ''), () => {
                invoiceListClearFilters(ROUTES.INVOICES.ESCALATED_HOSPITAL);
              })}
            </MenuItem>
            {/*<MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.PARSE_HOSPITAL)}>*/}
            {/*  Upload Hospital Invoice{this.getWrappedLink(ROUTES.INVOICES.PARSE_HOSPITAL)}*/}
            {/*</MenuItem>*/}
            <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.HOSPITAL_BULK_CHASE)}>
              Bulk Chase Invoices{this.getWrappedLink(ROUTES.INVOICES.HOSPITAL_BULK_CHASE)}
            </MenuItem>
            <MenuItem active={isRouteActive(location.pathname, ROUTES.EMAIL_RESPONSES.ALL.replace('/:page', ''))}>
              Chaser Email Replies{this.getWrappedLink(ROUTES.EMAIL_RESPONSES.ALL.replace('/:page', ''))}
            </MenuItem>
          </SubMenu>

          {/*<SubMenu*/}
          {/*  className={' disabled'}*/}
          {/*  title="Insurance Chasing"*/}
          {/*  //                     icon={<i className="fas fa-file-invoice"/>}*/}
          {/*>*/}
          {/*  <MenuItem*/}
          {/*    className={' disabled'}*/}

          {/*    //                  suffix={*/}
          {/*    //                    stats.escalatedSelfPayInvoices && stats.escalatedSelfPayInvoices > 0 ? (*/}
          {/*    //                        <span className="badge badge-pill badge-danger">{stats.escalatedSelfPayInvoices}</span>*/}
          {/*    //                    ) : (*/}
          {/*    //                        ''*/}
          {/*    //                    )*/}
          {/*    //                  }*/}
          {/*    //                  active={isRouteActive(location.pathname, ROUTES.INVOICES.ESCALATED.replace('/:page', ''))}*/}
          {/*  >*/}
          {/*    Escalated Invoices*/}
          {/*    /!*  {this.getWrappedLink(ROUTES.INVOICES.ESCALATED.replace('/:page', ''), () => {*!/*/}
          {/*    /!*  invoiceListClearFilters(ROUTES.INVOICES.ESCALATED);*!/*/}
          {/*    /!*})}*!/*/}
          {/*  </MenuItem>*/}
          {/*  <MenuItem*/}
          {/*    className={' disabled'}*/}
          {/*    //                  active={isRouteActive(location.pathname, ROUTES.INVOICES.PARSE)}*/}
          {/*  >*/}
          {/*    Upload Insurance Invoice*/}
          {/*    /!*{this.getWrappedLink(ROUTES.INVOICES.PARSE)}*!/*/}
          {/*  </MenuItem>*/}
          {/*</SubMenu>*/}
        </SubMenu>

        <SubMenu title="Extended Workflow" icon={<i className="fas fa-columns"></i>}>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.EMBASSY.replace('/:page', ''))}>
            Embassy Invoice Batches
            {this.getWrappedLink(ROUTES.INVOICE_BATCHES.EMBASSY.replace('/:page', ''), () => {
              invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.EMBASSY);
            })}
          </MenuItem>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.PROBLEM_DATA.ALL.replace('/:page', ''))}>
            Problem Medicode Invoices
            {this.getWrappedLink(ROUTES.PROBLEM_DATA.ALL.replace('/:page', ''), () => {
              problemDataListClearFilters(ROUTES.PROBLEM_DATA.ALL);
            })}
          </MenuItem>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.PROBLEM_DATA.COMPLETED.replace('/:page', ''))}>
            Complete Problem Invoices
            {this.getWrappedLink(ROUTES.PROBLEM_DATA.COMPLETED.replace('/:page', ''), () => {
              problemDataListClearFilters(ROUTES.PROBLEM_DATA.COMPLETED);
            })}
          </MenuItem>
        </SubMenu>
      </>
    );
  }

  navReportsSubMenu() {
    const { location } = this.props;
    return (
      <SubMenu title="Reports" icon={<i className="fas fa-envelope" />}>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.REPORTS.ALL.replace('/:page', ''))}>
          View all Reports{this.getWrappedLink(ROUTES.REPORTS.ALL.replace('/:page', ''))}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.REPORTS.CREATE)}>Upload Reports{this.getWrappedLink(ROUTES.REPORTS.CREATE)}</MenuItem>
      </SubMenu>
    );
  }

  navConsultantTicketsSubMenu() {
    const { stats, location } = this.props;
    return (
      <SubMenu
        title="Consultant Tickets"
        icon={<i className="fas fa-ticket-alt" />}
        suffix={stats.unactionedTickets && stats.unactionedTickets > 0 ? <span className="badge badge-pill badge-danger">{stats.unactionedTickets}</span> : ''}>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CONSULTANT_TICKETS.ALL_COMMUNICATIONS.replace('/:page?', ''))}>
          All Communications{this.getWrappedLink(ROUTES.CONSULTANT_TICKETS.ALL_COMMUNICATIONS.replace('/:page?', ''))}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CONSULTANT_TICKETS.CREATE)}>
          Create a Consultant Ticket{this.getWrappedLink(ROUTES.CONSULTANT_TICKETS.CREATE)}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CONSULTANT_TICKETS.BROADCAST)}>
          Create a Consultant Broadcast{this.getWrappedLink(ROUTES.CONSULTANT_TICKETS.BROADCAST)}
        </MenuItem>
      </SubMenu>
    );
  }

  navConsultantItems() {
    const { consultantListClearFilters, location } = this.props;
    return (
      <>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CONSULTANTS.ALL.replace('/:page', ''))}>
          View Consultants
          {this.getWrappedLink(ROUTES.CONSULTANTS.ALL.replace('/:page', ''), () => {
            consultantListClearFilters(ROUTES.CONSULTANTS.ALL);
          })}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CONSULTANTS.CREATE)}>
          Create a Consultant Account{this.getWrappedLink(ROUTES.CONSULTANTS.CREATE)}
        </MenuItem>
        {isAdmin() && (
          <MenuItem active={isRouteActive(location.pathname, ROUTES.CASHBOOK_IMPORT)}>
            Import Cashbook Balances{this.getWrappedLink(ROUTES.CASHBOOK_IMPORT)}
          </MenuItem>
        )}
      </>
    );
  }

  navUserItems() {
    const { userListClearFilters, location } = this.props;
    return (
      <>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.USERS.ALL.replace('/:page', ''))}>
          View Users
          {this.getWrappedLink(ROUTES.USERS.ALL.replace('/:page', ''), () => {
            userListClearFilters(ROUTES.USERS.ALL);
          })}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.USERS.CREATE)}>Create a New User{this.getWrappedLink(ROUTES.USERS.CREATE)}</MenuItem>
      </>
    );
  }

  navHospitalItems() {
    const { location } = this.props;
    return (
      <>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.HOSPITALS.ALL.replace('/:page', ''))}>
          View Hospitals{this.getWrappedLink(ROUTES.HOSPITALS.ALL.replace('/:page', ''))}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.HOSPITALS.CREATE)}>Create a Hospital{this.getWrappedLink(ROUTES.HOSPITALS.CREATE)}</MenuItem>
      </>
    );
  }

  navInsurerItem() {
    const { location } = this.props;
    return (
      <MenuItem active={isRouteActive(location.pathname, ROUTES.HEALTH_INSURERS.ALL.replace('/:page?', ''))}>
        View Insurers{this.getWrappedLink(ROUTES.HEALTH_INSURERS.ALL.replace('/:page?', ''))}
      </MenuItem>
    );
  }

  navCcsdItems() {
    const { ccsdCodeListClearFilters, location } = this.props;
    return (
      <>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CCSD_CODES.ALL.replace('/:page', ''))}>
          View CCSD codes
          {this.getWrappedLink(ROUTES.CCSD_CODES.ALL.replace('/:page', ''), () => {
            ccsdCodeListClearFilters(ROUTES.CCSD_CODES.ALL);
          })}
        </MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CCSD_CHANGES.ALL.replace('/:page?', ''))}>
          CCSD Cost Changes Report{this.getWrappedLink(ROUTES.CCSD_CHANGES.ALL.replace('/:page?', ''))}
        </MenuItem>
      </>
    );
  }

  navGuidanceItems() {
    const { location } = this.props;
    return (
      <>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CMS.REMITTANCES)}>Remittances{this.getWrappedLink(ROUTES.CMS.REMITTANCES)}</MenuItem>
        {/*<MenuItem active={isRouteActive(location.pathname, ROUTES.CMS.HOSPITAL)}>Hospital{this.getWrappedLink(ROUTES.CMS.HOSPITAL)}</MenuItem>*/}
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CMS.INSURANCE)}>Insurance{this.getWrappedLink(ROUTES.CMS.INSURANCE)}</MenuItem>
        <MenuItem active={isRouteActive(location.pathname, ROUTES.CMS.EMBASSY)}>Embassy{this.getWrappedLink(ROUTES.CMS.EMBASSY)}</MenuItem>
      </>
    );
  }

  /**
   * The one definition of the patients entry. Top-level placements pass an icon; inside the
   * Records submenu, where nothing else has one, it goes without.
   */
  navPatientsItem(icon = null) {
    const { location } = this.props;
    return (
      <MenuItem icon={icon} active={isRouteActive(location.pathname, ROUTES.PATIENTS.ALL.replace('/:page?', ''))}>
        View Patients{this.getWrappedLink(ROUTES.PATIENTS.ALL.replace('/:page?', ''))}
      </MenuItem>
    );
  }

  /**
   * MED-1002. Sits beside View Patients, but is **senior staff and above only** (Dan,
   * 14/09/2026) - narrower than the register it sits next to.
   *
   * The role test lives here rather than at each of the three call sites on purpose. Gating it
   * per placement is how a menu entry ends up visible in one menu and not another, and this one
   * has to agree with `authByPath` and with `SuppressedShortfall::READ_SECURITY` on the API. One
   * rule, three placements, nothing to keep in step by hand.
   *
   * `isSeniorStaff()` non-strict, so admins arrive through it - matching the API expression,
   * which names ROLE_ADMIN explicitly for the same reason.
   *
   * Returns null rather than rendering a disabled entry: a link to a screen that answers 403 is
   * worse than no link.
   */
  navSuppressedShortfallsItem(icon = null) {
    const { location } = this.props;

    if (!isSeniorStaff()) {
      return null;
    }

    return (
      <MenuItem icon={icon} active={isRouteActive(location.pathname, ROUTES.SUPPRESSED_SHORTFALLS.ALL.replace('/:page?', ''))}>
        Suppressed Shortfalls{this.getWrappedLink(ROUTES.SUPPRESSED_SHORTFALLS.ALL.replace('/:page?', ''))}
      </MenuItem>
    );
  }

  renderStaffMenu() {
    const { stats, invoiceBatchListClearFilters, invoiceListClearFilters, consultantListClearFilters, location } = this.props;
    return (
      <>
        <MenuItem
          icon={<i className="fas fa-clock text-warning" />}
          suffix={
            stats.urgentInvoiceBatchesToComplete && stats.urgentInvoiceBatchesToComplete > 0 ? (
              <span className="badge badge-pill badge-danger">{stats.urgentInvoiceBatchesToComplete}</span>
            ) : (
              ''
            )
          }
          active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.URGENT.replace('/:page', ''))}>
          Urgent Invoice Batches
          {this.getWrappedLink(ROUTES.INVOICE_BATCHES.URGENT.replace('/:page', ''), () => {
            invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.URGENT);
          })}
        </MenuItem>
        <MenuItem
          icon={<i className="fas fa-inbox" />}
          suffix={stats.invoicesToAccept && stats.invoicesToAccept > 0 ? <span className="badge badge-pill badge-danger">{stats.invoicesToAccept}</span> : ''}
          active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.TO_ACCEPT.replace('/:page', ''))}>
          My Invoices to Accept{this.getWrappedLink(ROUTES.INVOICE_BATCHES.TO_ACCEPT.replace('/:page', ''))}
        </MenuItem>
        <MenuItem
          icon={<i className="fas fa-spinner" />}
          suffix={
            stats.invoicesToProcess && stats.invoicesToProcess > 0 ? <span className="badge badge-pill badge-danger">{stats.invoicesToProcess}</span> : ''
          }
          active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.TO_PROCESS.replace('/:page', ''))}>
          My Invoices to Process{this.getWrappedLink(ROUTES.INVOICE_BATCHES.TO_PROCESS.replace('/:page', ''))}
        </MenuItem>
        <MenuItem icon={<i className="fas fa-list" />} active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''))}>
          All Invoice Batches
          {this.getWrappedLink(ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''), () => {
            invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.ALL);
          })}
        </MenuItem>
        <MenuItem icon={<i className="fas fa-upload" />} active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.CREATE)}>
          Upload Invoices{this.getWrappedLink(ROUTES.INVOICE_BATCHES.CREATE)}
        </MenuItem>
        <SubMenu title="Credit Control" icon={<i className="fas fa-file-invoice" />}>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.UNPAID.replace('/:page', ''))}>
            UnPaid Invoices
            {this.getWrappedLink(ROUTES.INVOICES.UNPAID.replace('/:page', ''), () => {
              invoiceListClearFilters(ROUTES.INVOICES.UNPAID);
            })}
          </MenuItem>
          <MenuItem
            suffix={
              stats.invoicesCurrentlyAssigned && stats.invoicesCurrentlyAssigned > 0 ? (
                <span className="badge badge-pill badge-danger">{stats.invoicesCurrentlyAssigned}</span>
              ) : (
                ''
              )
            }
            active={isRouteActive(location.pathname, ROUTES.INVOICES.MY_ASSIGNED.replace('/:page', ''))}>
            CC Invoices
            {this.getWrappedLink(ROUTES.INVOICES.MY_ASSIGNED.replace('/:page', ''), () => {
              invoiceListClearFilters(ROUTES.INVOICES.MY_ASSIGNED);
            })}
          </MenuItem>
          <MenuItem
            suffix={
              stats.escalatedSelfPayInvoices && stats.escalatedSelfPayInvoices > 0 ? (
                <span className="badge badge-pill badge-danger">{stats.escalatedSelfPayInvoices}</span>
              ) : (
                ''
              )
            }>
            Escalated Invoices
            {this.getWrappedLink(ROUTES.INVOICES.ESCALATED_SELF_PAY.replace('/:page', ''), () => {
              invoiceListClearFilters(ROUTES.INVOICES.ESCALATED_SELF_PAY);
            })}
          </MenuItem>
          <MenuItem
            suffix={
              stats.creditControlInvoicesMissingContactDetails && stats.creditControlInvoicesMissingContactDetails > 0 ? (
                <span className="badge badge-pill badge-danger">{stats.creditControlInvoicesMissingContactDetails}</span>
              ) : (
                ''
              )
            }>
            Missing Contact Details
            {this.getWrappedLink(ROUTES.INVOICES.MISSING_CONTACT_DETAILS.replace('/:page', ''), () => {
              invoiceListClearFilters(ROUTES.INVOICES.MISSING_CONTACT_DETAILS);
            })}
          </MenuItem>
        </SubMenu>
        {/*<SubMenu title="Accounts" icon={<i className='fas fa-user'/>}>*/}
        <MenuItem icon={<i className="fas fa-user-md" />} active={isRouteActive(location.pathname, ROUTES.CONSULTANTS.ALL.replace('/:page', ''))}>
          View Consultants
          {this.getWrappedLink(ROUTES.CONSULTANTS.ALL.replace('/:page', ''), () => {
            consultantListClearFilters(ROUTES.CONSULTANTS.ALL);
          })}
        </MenuItem>
        {/*<MenuItem>View Users{this.getWrappedLink(ROUTES.USERS.ALL.replace('/:page', ''))}</MenuItem>*/}
        {/*</SubMenu>*/}
        {this.navPatientsItem(<i className="fas fa-user-injured" />)}
        {this.navSuppressedShortfallsItem(<i className="fas fa-bell-slash" />)}
        <SubMenu title="Info" icon={<i className="fas fa-info" />}>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.CMS.REMITTANCES)}>Remittances{this.getWrappedLink(ROUTES.CMS.REMITTANCES)}</MenuItem>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.CMS.HOSPITAL)}>Hospital{this.getWrappedLink(ROUTES.CMS.HOSPITAL)}</MenuItem>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.CMS.INSURANCE)}>Insurance{this.getWrappedLink(ROUTES.CMS.INSURANCE)}</MenuItem>
          <MenuItem active={isRouteActive(location.pathname, ROUTES.CMS.EMBASSY)}>Embassy{this.getWrappedLink(ROUTES.CMS.EMBASSY)}</MenuItem>
        </SubMenu>
      </>
    );
  }

  renderOutsourcerMenu() {
    const { stats, invoiceBatchListClearFilters, invoiceListClearFilters, location } = this.props;
    return (
      <>
        <SubMenu title="Work Schedule" icon={<i className="fas fa-calendar-check"></i>}>
          {isOutsourcer(true) && (
            <MenuItem active={isRouteActive(location.pathname, ROUTES.WORK_SCHEDULE.replace('/:date', ''))}>
              Batch Work Schedule{this.getWrappedLink(ROUTES.WORK_SCHEDULE.replace('/:date', ''))}
            </MenuItem>
          )}
          {isChaserOutsourcer(true) && (
            <MenuItem active={isRouteActive(location.pathname, ROUTES.CHASER_WORK_SCHEDULE.replace('/:date', ''))}>
              Invoice Work Schedule{this.getWrappedLink(ROUTES.CHASER_WORK_SCHEDULE.replace('/:date', ''))}
            </MenuItem>
          )}
          {isOutsourcer(true) && (
            <MenuItem active={isRouteActive(location.pathname, ROUTES.WORK_SCHEDULE_EVENTS.ALL.replace('/:page', ''))}>
              View my Holidays{this.getWrappedLink(ROUTES.WORK_SCHEDULE_EVENTS.ALL.replace('/:page', ''))}
            </MenuItem>
          )}
          {isOutsourcer(true) && (
            <MenuItem active={isRouteActive(location.pathname, ROUTES.WORK_SCHEDULE_EVENTS.CREATE)}>
              Add a Holiday{this.getWrappedLink(ROUTES.WORK_SCHEDULE_EVENTS.CREATE)}
            </MenuItem>
          )}
        </SubMenu>

        {isOutsourcer(true) && (
          <SubMenu defaultOpen={true} title="Invoice Batches" icon={<i className="fas fa-calendar-check"></i>}>
            <MenuItem
              suffix={
                stats.invoicesToAccept && stats.invoicesToAccept > 0 ? <span className="badge badge-pill badge-danger">{stats.invoicesToAccept}</span> : ''
              }
              active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.TO_ACCEPT.replace('/:page', ''))}>
              Batches to Accept
              {this.getWrappedLink(ROUTES.INVOICE_BATCHES.TO_ACCEPT.replace('/:page', ''), () => {
                invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.TO_ACCEPT);
              })}
            </MenuItem>
            <MenuItem
              suffix={
                stats.invoicesToProcess && stats.invoicesToProcess > 0 ? <span className="badge badge-pill badge-danger">{stats.invoicesToProcess}</span> : ''
              }
              active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.TO_PROCESS.replace('/:page', ''))}>
              Batches to Process
              {this.getWrappedLink(ROUTES.INVOICE_BATCHES.TO_PROCESS.replace('/:page', ''), () => {
                invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.TO_PROCESS);
              })}
            </MenuItem>
            <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''))}>
              All Invoice Batches
              {this.getWrappedLink(ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''), () => {
                invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.ALL);
              })}
            </MenuItem>
          </SubMenu>
        )}

        {isChaserOutsourcer(true) && (
          <SubMenu defaultOpen={true} title="Invoices" icon={<i className="fas fa-calendar-check"></i>}>
            <MenuItem
              suffix={
                stats.invoicesCurrentlyAssigned && stats.invoicesCurrentlyAssigned > 0 ? (
                  <span className="badge badge-pill badge-danger">{stats.invoicesCurrentlyAssigned}</span>
                ) : (
                  ''
                )
              }
              active={isRouteActive(location.pathname, ROUTES.INVOICES.MY_ASSIGNED.replace('/:page', ''))}>
              Assigned Invoices{' '}
              {this.getWrappedLink(ROUTES.INVOICES.MY_ASSIGNED.replace('/:page', ''), () => {
                invoiceListClearFilters(ROUTES.INVOICES.MY_ASSIGNED);
              })}
            </MenuItem>
            {canUploadInvoices() && (
              <MenuItem active={isRouteActive(location.pathname, ROUTES.INVOICES.PARSE_SELF_PAY)}>
                Upload Shortfall/Rebill {this.getWrappedLink(ROUTES.INVOICES.PARSE_SELF_PAY)}
              </MenuItem>
            )}
          </SubMenu>
        )}

        {isOutsourcer(true) && (
          <>
            <SubMenu title="Info" icon={<i className="fas fa-info" />}>
              <MenuItem active={isRouteActive(location.pathname, ROUTES.CMS.INSURANCE)}>Insurance{this.getWrappedLink(ROUTES.CMS.INSURANCE)}</MenuItem>
            </SubMenu>
          </>
        )}
      </>
    );
  }

  renderConsultantMenu() {
    const { userData, invoiceBatchListClearFilters, location } = this.props;
    return (
      <>
        <MenuItem icon={<i className="fas fa-list" />} active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''))}>
          All Invoices
          {this.getWrappedLink(ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''), () => {
            invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.ALL);
          })}
        </MenuItem>
        <MenuItem icon={<i className="fas fa-upload" />} active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.CREATE)}>
          Upload Invoices{this.getWrappedLink(ROUTES.INVOICE_BATCHES.CREATE)}
        </MenuItem>
        <MenuItem icon={<i className="fas fa-envelope" />} active={isRouteActive(location.pathname, ROUTES.REPORTS.ALL.replace('/:page', ''))}>
          View My Reports{this.getWrappedLink(ROUTES.REPORTS.ALL.replace('/:page', ''))}
        </MenuItem>
        <MenuItem
          icon={<i className="fas fa-ticket-alt" />}
          active={isRouteActive(location.pathname, ROUTES.CONSULTANT_TICKETS.ALL_TICKETS.replace('/:page', ''))}>
          View My Tickets{this.getWrappedLink(ROUTES.CONSULTANT_TICKETS.ALL_TICKETS.replace('/:page', ''))}
        </MenuItem>
        <MenuItem icon={<i className="fas fa-plus" />} active={isRouteActive(location.pathname, ROUTES.CONSULTANT_TICKETS.CREATE)}>
          Create a Ticket{this.getWrappedLink(ROUTES.CONSULTANT_TICKETS.CREATE)}
        </MenuItem>
        {userData && userData.consultantAsPrimaryUser && (
          <MenuItem
            icon={<i className="fas fa-user-cog" />}
            active={isRouteActive(location.pathname, ROUTES.CONSULTANTS.SINGLE.replace(':id', userData.consultantAsPrimaryUser.id))}>
            Settings and Users{this.getWrappedLink(ROUTES.CONSULTANTS.SINGLE.replace(':id', userData.consultantAsPrimaryUser.id))}
          </MenuItem>
        )}
      </>
    );
  }

  renderSubUserMenu() {
    const { invoiceBatchListClearFilters, location } = this.props;
    return (
      <>
        {subUserCanViewAndDownloadInvoices() ? (
          <MenuItem icon={<i className="fas fa-list" />} active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''))}>
            All Invoices
            {this.getWrappedLink(ROUTES.INVOICE_BATCHES.ALL.replace('/:page', ''), () => {
              invoiceBatchListClearFilters(ROUTES.INVOICE_BATCHES.ALL);
            })}
          </MenuItem>
        ) : (
          <></>
        )}
        {subUserCanUploadBills() ? (
          <MenuItem icon={<i className="fas fa-upload" />} active={isRouteActive(location.pathname, ROUTES.INVOICE_BATCHES.CREATE)}>
            Upload Invoices{this.getWrappedLink(ROUTES.INVOICE_BATCHES.CREATE)}
          </MenuItem>
        ) : (
          <></>
        )}
        {subUserCanViewAndDownloadReports() ? (
          <MenuItem icon={<i className="fas fa-envelope" />} active={isRouteActive(location.pathname, ROUTES.REPORTS.ALL.replace('/:page', ''))}>
            View Reports{this.getWrappedLink(ROUTES.REPORTS.ALL.replace('/:page', ''))}
          </MenuItem>
        ) : (
          <></>
        )}
        <MenuItem icon={<i className="fas fa-user-md" />} active={isRouteActive(location.pathname, ROUTES.CONSULTANTS.SINGLE_FOR_SUBUSER)}>
          Consultant Details{this.getWrappedLink(ROUTES.CONSULTANTS.SINGLE_FOR_SUBUSER)}
        </MenuItem>
        <MenuItem
          icon={<i className="fas fa-ticket-alt" />}
          active={isRouteActive(location.pathname, ROUTES.CONSULTANT_TICKETS.ALL_TICKETS.replace('/:page', ''))}>
          Consultant Tickets{this.getWrappedLink(ROUTES.CONSULTANT_TICKETS.ALL_TICKETS.replace('/:page', ''))}
        </MenuItem>
        <MenuItem icon={<i className="fas fa-plus" />} active={isRouteActive(location.pathname, ROUTES.CONSULTANT_TICKETS.CREATE)}>
          Create a Ticket{this.getWrappedLink(ROUTES.CONSULTANT_TICKETS.CREATE)}
        </MenuItem>
      </>
    );
  }

  render() {
    const { userLogout, shouldBeVisible, stats, userData, location } = this.props;
    const sidebarClasses = clsx('mediaaccounts-sidebar d-print-none', {
      show: shouldBeVisible,
    });
    // Only the admin menu has two arrangements - renderStaffMenu, renderOutsourcerMenu and the
    // client menus are identical either way, so for those roles the toggle would change nothing
    // visible. Same expression as the renderAdminMenu() gate below, so the two cannot drift.
    const showNavToggle = isAdmin() || isSeniorStaff(true);
    const showVersions = Boolean(process.env.REACT_APP_PORTAL_VERSION && stats?.apiVersion);

    return (
      <ProSidebar className={sidebarClasses}>
        <SidebarContent>
          <Menu iconShape="circle" innerSubMenuArrows={true}>
            <MenuItem icon={<i className="fas fa-home" />} active={isRouteActive(location.pathname, ROUTES.HOME)}>
              Dashboard{this.getWrappedLink(ROUTES.HOME)}
            </MenuItem>

            {process.env.NODE_ENV !== 'production' && isConsultant(true) && !userData.consultantAsPrimaryUser ? (
              <>
                <MenuItem icon={<i className="fas fa-sign-out-alt" />}>User has no consultant</MenuItem>
                <MenuItem icon={<i className="fas fa-sign-out-alt" />} onClick={userLogout}>
                  Log Out
                </MenuItem>
              </>
            ) : (
              <>
                {(isAdmin() || isSeniorStaff(true)) && this.renderAdminMenu()}
                {isStaff(true) && this.renderStaffMenu()}
                {(isOutsourcer(true) || isChaserOutsourcer(true)) && this.renderOutsourcerMenu()}
                {isConsultant(true) && this.renderConsultantMenu()}
                {isSubUser(true) && this.renderSubUserMenu()}

                {/*
                  MED-921. Top level rather than inside a role menu, because the patient register
                  is the one screen every internal role shares - staff, senior staff, admins and
                  outsourcers alike - while the Accounts submenu that holds Hospitals and Users is
                  admin and senior staff only.

                  This test has to stay in step with authByPath and with Patient::READ_SECURITY on
                  the API; a link a user cannot follow is worse than no link. isStaff() is
                  non-strict so it covers admins and senior staff, and the two outsourcer checks
                  together are what isAnyOutsourcer() means - spelled out here because Sidebar
                  already imports these three and its test mocks exactly them.
                */}
                {/*
                  The fallback placement, for roles whose own menu does not carry it:
                  outsourcers, and admins and senior staff on the old structure.

                  Not for strict ROLE_STAFF - renderStaffMenu puts it above Info, where it sits
                  with the other registers rather than below the guidance pages. Not for admins
                  and senior staff on the new structure either, who reach it through Records.
                */}
                {(isOutsourcer() || isChaserOutsourcer() || isAdmin() || isSeniorStaff(true)) &&
                  !isStaff(true) &&
                  !(isNewNav() && (isAdmin() || isSeniorStaff(true))) &&
                  this.navPatientsItem(<i className="fas fa-user-injured" />)}

                {(isOutsourcer() || isChaserOutsourcer() || isAdmin() || isSeniorStaff(true)) &&
                  !isStaff(true) &&
                  !(isNewNav() && (isAdmin() || isSeniorStaff(true))) &&
                  this.navSuppressedShortfallsItem(<i className="fas fa-bell-slash" />)}

                {isClient() && (
                  <MenuItem
                    icon={<i className="fas fa-user-plus" />}
                    onClick={() => {
                      window.open('https://form.typeform.com/to/T7GOL1wa', '_blank', 'noopener,noreferrer');
                      this.props.toggleSidebar();
                    }}>
                    Refer a Colleague
                  </MenuItem>
                )}

                <MenuItem icon={<i className="fas fa-user-cog" />} active={isRouteActive(location.pathname, ROUTES.PROFILE)}>
                  Edit Profile{this.getWrappedLink(ROUTES.PROFILE)}
                </MenuItem>
                <MenuItem icon={<i className="fas fa-question" />} active={isRouteActive(location.pathname, ROUTES.SUPPORT)}>
                  Help?{this.getWrappedLink(ROUTES.SUPPORT)}
                </MenuItem>
                <MenuItem icon={<i className="fas fa-sign-out-alt" />} onClick={userLogout}>
                  Log Out
                </MenuItem>
              </>
            )}
          </Menu>
        </SidebarContent>
        {(showNavToggle || showVersions) && (
          <SidebarFooter className="text-center">
            {/*
              The new/old sidebar toggle lives down here rather than in the menu, because it is
              not somewhere you go - it is a preference you set once and forget. In the list it
              read as another destination, and sat above Log Out competing with things people
              actually click.

              Offered only where the two structures actually differ - the admin and senior staff
              menu. Staff, outsourcers and client-side users see the same items either way, so a
              switch for them would change nothing visible and only invite a support call. See
              showNavToggle.

              It writes straight to localStorage and bumps local state to re-render, rather than
              going through redux - nothing else in the app needs to know, and a choice that
              survives a refresh is the whole point of trialling one layout against another.
            */}
            {showNavToggle && (
              <button type="button" className="sidebar__nav-toggle" onClick={this.toggleNavStructure.bind(this)}>
                <i className={`fas ${isNewNav() ? 'fa-toggle-on' : 'fa-toggle-off'} mr-1`} aria-hidden="true" />
                {isNewNav() ? 'Back to the old menu' : 'Try the new menu'}
              </button>
            )}
            {showVersions && (
              <p className="sidebar__version">
                Portal {process.env.REACT_APP_PORTAL_VERSION} / API {stats.apiVersion}
              </p>
            )}
          </SidebarFooter>
        )}
      </ProSidebar>
    );
  }
}

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(Sidebar);
